import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X, ArrowDownUp } from "lucide-react";

/**
 * Phase 18 — Generic admin filter panel.
 *
 * Props:
 *   schemas: filter schema array (see useFilters)
 *   values:  current values
 *   set:     (key, value) => void
 *   clear:   () => void
 *   count:   filtered count
 *   total:   total count (optional)
 *   activeCount: number of non-default filters
 *   sortFields: [{ key, label }]  for the sort dropdown
 *   sort:    { field, dir } | null
 *   setSort: ({ field, dir }) => void
 *   testid:  data-testid prefix for the panel container
 */
export function FilterPanel({
  schemas, values, set, clear, count, total, activeCount,
  sortFields = [], sort, setSort, testid = "filter-panel",
}) {
  const searchSchema = schemas.find((s) => s.type === "search");
  const otherSchemas = schemas.filter((s) => s.type !== "search");

  const sortValue = sort?.field ? `${sort.field}|${sort.dir}` : "";
  const sortOptions = sortFields.flatMap((f) => [
    { value: `${f.key}|desc`, label: `${f.label} (newest first)` },
    { value: `${f.key}|asc`, label: `${f.label} (oldest first)` },
  ]);

  return (
    <Card className="p-4 border-slate-200 mb-5 space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-3">
        {searchSchema && (
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={values[searchSchema.key] || ""}
              onChange={(e) => set(searchSchema.key, e.target.value)}
              placeholder={searchSchema.label || "Search…"}
              className="pl-9"
              data-testid={`${testid}-search`}
            />
          </div>
        )}
        {sortFields.length > 0 && (
          <div className="flex items-center gap-2">
            <ArrowDownUp className="h-4 w-4 text-slate-400" />
            <Select
              value={sortValue || "none"}
              onValueChange={(v) => {
                if (v === "none") return setSort?.(null);
                const [field, dir] = v.split("|");
                setSort?.({ field, dir });
              }}
            >
              <SelectTrigger className="h-9 w-[240px] text-sm" data-testid={`${testid}-sort`}>
                <SelectValue placeholder="Sort by…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Default order</SelectItem>
                {sortOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex-1" />
        <div className="text-xs text-slate-500" data-testid={`${testid}-count`}>
          {typeof total === "number" && total !== count ? `${count} of ${total}` : count} result{count === 1 ? "" : "s"}
        </div>
        {(activeCount > 0 || sort) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { clear(); setSort?.(null); }}
            className="text-xs text-slate-600"
            data-testid={`${testid}-clear`}
          >
            <X className="h-3.5 w-3.5 mr-1" /> Clear {activeCount > 0 ? `(${activeCount})` : ""}
          </Button>
        )}
      </div>

      {otherSchemas.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 pt-1">
          {otherSchemas.map((schema) => (
            <FilterControl key={schema.key} schema={schema} value={values[schema.key]} onChange={(v) => set(schema.key, v)} testid={testid} />
          ))}
        </div>
      )}
    </Card>
  );
}

function FilterControl({ schema, value, onChange, testid }) {
  const labelEl = <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{schema.label}</Label>;

  if (schema.type === "select") {
    return (
      <div className="space-y-1">
        {labelEl}
        <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-sm" data-testid={`${testid}-${schema.key}`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {schema.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (schema.type === "boolean") {
    return (
      <div className="space-y-1">
        {labelEl}
        <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-sm" data-testid={`${testid}-${schema.key}`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (schema.type === "dateFrom" || schema.type === "dateTo" || schema.type === "min" || schema.type === "max") {
    const isDate = schema.type.startsWith("date");
    return (
      <div className="space-y-1">
        {labelEl}
        <Input
          type={isDate ? "date" : "number"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 text-sm"
          data-testid={`${testid}-${schema.key}`}
        />
      </div>
    );
  }

  if (schema.type === "text") {
    return (
      <div className="space-y-1">
        {labelEl}
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={schema.placeholder || ""}
          className="h-9 text-sm"
          data-testid={`${testid}-${schema.key}`}
        />
      </div>
    );
  }

  return null;
}
